/**
 * Tidio chat layering override
 *
 * Tidio injects #tidio-chat with an inline z-index of 999999999 !important,
 * which stacks above theme drawers and commerce UI. Stylesheet rules cannot
 * beat inline !important, so this script re-applies theme-safe layering after
 * the widget loads and hides the widget when overlays or sticky controls are active.
 */
(function () {
  'use strict';

  const TIDIO_Z_INDEX = '15';
  const TIDIO_SELECTORS = ['#tidio-chat', '#tidio-chat-iframe'];
  const STYLE_SNAPSHOT_ATTR = 'data-tidio-theme-style-snapshot';
  const THEME_HIDDEN_ATTR = 'data-tidio-theme-hidden';
  const OVERLAY_SELECTOR = [
    '.drawer[open]',
    '.drawer[active]',
    '.quick-view[open]',
    '.quick-view[active]',
    '.route-modal.rm-open',
  ].join(', ');

  let initialized = false;
  let discoveryObserver = null;
  let observedRouteModal = null;
  let routeModalClassObserver = null;
  let routeModalDiscoveryObserver = null;
  const tidioStyleObservers = new Map();

  const getTidioElements = () => {
    return TIDIO_SELECTORS.flatMap((selector) => [...document.querySelectorAll(selector)]);
  };

  const isStickyCommerceVisible = () => {
    const stickyCard = document.querySelector('.product-sticky-form__card');

    if (stickyCard) {
      const style = getComputedStyle(stickyCard);

      if (style.visibility === 'visible' && Number.parseFloat(style.opacity) > 0) {
        return true;
      }
    }

    return Boolean(document.querySelector('.product-bundle-wrapper > .lg\\:grow-0.active'));
  };

  const shouldHideTidio = () => {
    if (document.body.classList.contains('has-modal-open')) {
      return true;
    }

    if (document.querySelector(OVERLAY_SELECTOR)) {
      return true;
    }

    return isStickyCommerceVisible();
  };

  const readInlineStyle = (element, property) => ({
    value: element.style.getPropertyValue(property),
    priority: element.style.getPropertyPriority(property),
  });

  const restoreInlineStyle = (element, property, snapshot) => {
    if (!snapshot?.value) {
      element.style.removeProperty(property);
      return;
    }

    element.style.setProperty(property, snapshot.value, snapshot.priority);
  };

  const captureStyleSnapshot = (element) => {
    if (element.hasAttribute(STYLE_SNAPSHOT_ATTR)) {
      return;
    }

    element.setAttribute(
      STYLE_SNAPSHOT_ATTR,
      JSON.stringify({
        visibility: readInlineStyle(element, 'visibility'),
        pointerEvents: readInlineStyle(element, 'pointer-events'),
      })
    );
  };

  const restoreStyleSnapshot = (element) => {
    const rawSnapshot = element.getAttribute(STYLE_SNAPSHOT_ATTR);

    if (!rawSnapshot) {
      return;
    }

    try {
      const snapshot = JSON.parse(rawSnapshot);
      restoreInlineStyle(element, 'visibility', snapshot.visibility);
      restoreInlineStyle(element, 'pointer-events', snapshot.pointerEvents);
    } finally {
      element.removeAttribute(STYLE_SNAPSHOT_ATTR);
    }
  };

  const applyToElement = (element) => {
    if (element.style.getPropertyValue('z-index') !== TIDIO_Z_INDEX) {
      element.style.setProperty('z-index', TIDIO_Z_INDEX, 'important');
    }

    const hidden = shouldHideTidio();
    const themeHidden = element.hasAttribute(THEME_HIDDEN_ATTR);

    if (hidden) {
      if (!themeHidden) {
        captureStyleSnapshot(element);
        element.setAttribute(THEME_HIDDEN_ATTR, '');
      }

      element.style.setProperty('visibility', 'hidden', 'important');
      element.style.setProperty('pointer-events', 'none', 'important');
      return;
    }

    if (themeHidden) {
      element.removeAttribute(THEME_HIDDEN_ATTR);
      restoreStyleSnapshot(element);
    }
  };

  const syncTidio = () => {
    getTidioElements().forEach(applyToElement);
  };

  const observeTidioElement = (element) => {
    if (tidioStyleObservers.has(element)) {
      return;
    }

    const observer = new MutationObserver(() => {
      applyToElement(element);
    });

    observer.observe(element, {
      attributes: true,
      attributeFilter: ['style'],
    });

    tidioStyleObservers.set(element, observer);
  };

  const disconnectDiscoveryObserver = () => {
    if (!discoveryObserver) {
      return;
    }

    discoveryObserver.disconnect();
    discoveryObserver = null;
  };

  const attachToTidioElements = () => {
    const elements = getTidioElements();

    if (!elements.length) {
      return false;
    }

    elements.forEach((element) => {
      observeTidioElement(element);
      applyToElement(element);
    });

    disconnectDiscoveryObserver();
    return true;
  };

  const startDiscoveryObserver = () => {
    if (discoveryObserver) {
      return;
    }

    discoveryObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList') {
          continue;
        }

        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) {
            continue;
          }

          if (TIDIO_SELECTORS.some((selector) => node.matches?.(selector) || node.querySelector?.(selector))) {
            attachToTidioElements();
            return;
          }
        }
      }
    });

    discoveryObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    window.setTimeout(disconnectDiscoveryObserver, 30000);
  };

  const resetRouteModalObserver = () => {
    if (routeModalClassObserver) {
      routeModalClassObserver.disconnect();
      routeModalClassObserver = null;
    }

    observedRouteModal = null;
  };

  const findRouteModalInNode = (node) => {
    if (node.nodeType !== 1) {
      return null;
    }

    if (node.classList.contains('route-modal')) {
      return node;
    }

    return node.querySelector?.('.route-modal') || null;
  };

  const watchRouteModalElement = (modal) => {
    if (!modal || modal === observedRouteModal) {
      return;
    }

    resetRouteModalObserver();
    observedRouteModal = modal;

    routeModalClassObserver = new MutationObserver(syncTidio);
    routeModalClassObserver.observe(modal, {
      attributes: true,
      attributeFilter: ['class'],
    });
  };

  const routeModalMutationIsRelevant = (mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.target.classList?.contains('route-modal')) {
        return true;
      }

      if (mutation.type !== 'childList') {
        continue;
      }

      for (const node of mutation.addedNodes) {
        if (findRouteModalInNode(node)) {
          return true;
        }
      }

      for (const node of mutation.removedNodes) {
        if (node.nodeType !== 1) {
          continue;
        }

        if (node === observedRouteModal || findRouteModalInNode(node)) {
          return true;
        }
      }
    }

    return false;
  };

  const syncRouteModalTracking = () => {
    if (observedRouteModal && !document.contains(observedRouteModal)) {
      resetRouteModalObserver();
    }

    watchRouteModalElement(document.querySelector('.route-modal'));
    syncTidio();
  };

  const startRouteModalTracking = () => {
    watchRouteModalElement(document.querySelector('.route-modal'));

    if (routeModalDiscoveryObserver) {
      return;
    }

    routeModalDiscoveryObserver = new MutationObserver((mutations) => {
      const observedMissing = observedRouteModal && !document.contains(observedRouteModal);

      if (!routeModalMutationIsRelevant(mutations) && !observedMissing) {
        return;
      }

      syncRouteModalTracking();
    });

    routeModalDiscoveryObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  const observeOverlayState = () => {
    document.addEventListener('modal:afterShow', syncTidio);
    document.addEventListener('modal:afterHide', syncTidio);

    new MutationObserver(syncTidio).observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });

    const stickyCard = document.querySelector('.product-sticky-form__card');

    if (stickyCard) {
      new MutationObserver(syncTidio).observe(stickyCard, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }

    const bundleSidebar = document.querySelector('.product-bundle-wrapper > .lg\\:grow-0');

    if (bundleSidebar) {
      new MutationObserver(syncTidio).observe(bundleSidebar, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    const hasStickyCommerceUI = document.querySelector('.product-sticky-form, .product-bundle-wrapper');

    if (hasStickyCommerceUI) {
      let scrollRaf = null;

      window.addEventListener(
        'scroll',
        () => {
          if (scrollRaf) {
            return;
          }

          scrollRaf = window.requestAnimationFrame(() => {
            scrollRaf = null;
            syncTidio();
          });
        },
        { passive: true }
      );
    }

    startRouteModalTracking();
  };

  const init = () => {
    if (!attachToTidioElements()) {
      startDiscoveryObserver();
    }

    if (initialized) {
      syncTidio();
      return;
    }

    initialized = true;
    observeOverlayState();
    syncTidio();

    let attempts = 0;
    const poll = window.setInterval(() => {
      attempts += 1;

      if (attachToTidioElements() || attempts >= 20) {
        window.clearInterval(poll);
      }
    }, 500);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (window.tidioChatApi) {
    window.tidioChatApi.on('ready', init);
  } else {
    document.addEventListener('tidioChat-ready', init);
  }
})();
